/** Private, resumable setup. Answers never enter browser storage or chat history. */
import { useEffect, useState } from "react";
import axios from "axios";
import UCEvidenceReview from "./UCEvidenceReview";

const ROOT = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const client = axios.create({ withCredentials: true, timeout: 20000 });
const URL = `${ROOT}/api/service-controls/universal_credit/setup`;

export default function UCSetup({ onSaved }) {
  const [setup, setSetup] = useState(null);
  const [edit, setEdit] = useState("");
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const [review, setReview] = useState(null);
  const [reviewError, setReviewError] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setSetup(null); setError(""); setReview(null); setReviewError("");
    client.get(URL, { signal: controller.signal }).then(({ data }) => {
      if (!Array.isArray(data?.questions) || !data.answers || !Number.isInteger(data.revision)
          || !Array.isArray(data.outstanding_checks) || data.active !== false) throw new Error("Invalid setup");
      if (active) { setSetup(data); setEdit(""); }
    }).catch(() => { if (active) setError("Setup could not be loaded. Refresh to try again."); });
    return () => { active = false; controller.abort(); };
  }, [reload]);
  const question = edit ? setup?.questions.find(q => q.key === edit) : setup?.next_question;
  useEffect(() => { setValue(setup?.answers?.[question?.key] || ""); }, [setup, question?.key]);

  const save = async event => {
    event.preventDefault();
    if (busy || !question || !value) return;
    setBusy(true); setError("");
    try {
      await client.put(URL, { key: question.key, value, expected_revision: setup.revision },
        { headers: { "x-ndg-service-control": "browser-v1" } });
      // Refresh the parent's revision as well as this question. Never retry a PUT.
      onSaved();
    } catch (_) {
      setError("Your answer was not confirmed. Refresh setup before trying again.");
    } finally { setBusy(false); }
  };

  const checkRecords = async () => {
    if (busy) return;
    setBusy(true); setReview(null); setReviewError("");
    try {
      const { data } = await client.get(`${URL}/source-review`);
      if (data?.setup_revision !== setup.revision || data.active !== false
          || data.ready_to_submit !== false || !Array.isArray(data.requirements)
          || typeof data.notice !== "string"
          || !data.requirements.every(item => typeof item.code === "string" && typeof item.message === "string"
            && Number.isInteger(item.count) && item.count > 0 && item.state === "unresolved")
          || !Array.isArray(data.circumstance_checks)) throw new Error("Invalid review");
      setReview(data);
    } catch (_) {
      setReviewError("Records could not be checked. Refresh setup and try again.");
    } finally { setBusy(false); }
  };

  return <div data-testid="uc-setup" className="space-y-3 border-t pt-3">
    <p className="font-semibold">Set up Universal Credit preparation</p>
    <p className="text-sm">You can leave and return. This does not connect accounts or submit anything.</p>
    {error && <div role="alert"><p>{error}</p><button type="button" onClick={() => setReload(n => n + 1)}>Refresh setup</button></div>}
    {!setup && !error && <p role="status">Loading your saved setup…</p>}
    {setup && <>
      <p className="text-sm">{setup.answered} of {setup.total} questions answered</p>
      {question && <form onSubmit={save} className="space-y-2">
        <label htmlFor="uc-setup-answer" className="block">{question.prompt}</label>
        {question.kind === "date" ? <input id="uc-setup-answer" type="date" required
          disabled={busy || Boolean(error)} value={value} onChange={e => setValue(e.target.value)}
          className="block w-full max-w-full p-3 border rounded bg-white text-slate-900 dark:bg-slate-800 dark:text-white" />
          : <select id="uc-setup-answer" required disabled={busy || Boolean(error)} value={value}
            onChange={e => setValue(e.target.value)}
            className="block w-full max-w-full p-3 border rounded bg-white text-slate-900 dark:bg-slate-800 dark:text-white">
            <option value="">Choose an answer</option>
            {question.options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>}
        <button type="submit" disabled={busy || !value || Boolean(error)} className="min-h-11 px-4 py-2 border rounded">
          {busy ? "Saving…" : "Save and continue"}
        </button>
      </form>}
      {!setup.next_question && !edit && <>
        <UCEvidenceReview key={setup.revision} setupRevision={setup.revision} />
        <p role="status">{setup.message}</p>
        <ul className="list-disc pl-5">{setup.outstanding_checks.map(check => <li key={check}>{check}</li>)}</ul>
        <button type="button" disabled={busy || Boolean(error)} onClick={checkRecords}
          className="min-h-11 px-4 py-2 border rounded">{busy ? "Checking records…" : "Check connected records"}</button>
        {reviewError && <div role="alert"><p>{reviewError}</p>
          <button type="button" disabled={busy} onClick={() => setReload(n => n + 1)}>Refresh setup</button></div>}
        {review && <div data-testid="uc-source-review" className="space-y-2">
          <p role="status">{review.notice}</p>
          <ul className="list-disc pl-5">{review.requirements.map(item =>
            <li key={item.code}>{item.message} ({item.count})</li>)}</ul>
        </div>}
      </>}
      {Object.keys(setup.answers).length > 0 && <>
        <label htmlFor="uc-setup-edit" className="block text-sm">Review or change a saved answer</label>
        <select id="uc-setup-edit" value={edit} disabled={busy || Boolean(error)} onChange={e => setEdit(e.target.value)}
          className="block w-full max-w-full p-3 border rounded bg-white text-slate-900 dark:bg-slate-800 dark:text-white">
          <option value="">Continue setup</option>
          {setup.questions.filter(q => Object.hasOwn(setup.answers, q.key)).map(q => <option key={q.key} value={q.key}>{q.prompt}</option>)}
        </select>
      </>}
    </>}
  </div>;
}
